import React, { useEffect, useState, useRef } from 'react'
import {
  CustomType,
  GoalId,
  GoalWithProgress,
  ProgressType,
  Role,
  UserCustomDailyProgress,
  UserDailyDiaryProgress,
  UserDailyProgress,
  UserProfileDto,
  DivisionCategoryRow,
} from '../../services/api.types'
import dayjs from '../../dayjsConfig'
import YumCustomTable from '../YumCustomTable/YumCustomTable'
import { CustomTypeCategory, getCustomTypeWithLegacy, getUnit } from '../../services/CustomTypes'
import { PluginListenerHandle } from '@capacitor/core'
import { App } from '@capacitor/app'
import { convertUTCToLocalDate } from '../../common/helpers/date'
import { syncChallengeProgress } from '../../common/helpers/challengeProgressSync'
import YumChallengeGoal from '../YumChallengeGoal/YumChallengeGoal'
import YumLogTable from '../YumCustomTable/YumLogTable'
import {
  getDailyValueGoal,
  getPersistenceDailyTotal,
  isActivityIndexType,
  isAutomatedType,
  isBonusType,
  isCollectiveType,
  isCustomType,
  isDistanceType,
  isForceCollectiveTotal,
  isPersistenceType,
  isSimpleDayType,
} from '../../common/helpers/challenges'
import YumChallengeActivityIndexProgress from '../YumChallengeGoal/components/YumChallengeActivityIndexProgress/YumChallengeActivityIndexProgress'
import YumChallengeCollectiveProgress from '../YumChallengeGoal/components/YumChallengeCollectiveProgress/YumChallengeCollectiveProgress'
import YumChallengeDailyProgress from '../YumChallengeGoal/components/YumChallengeDailyProgress/YumChallengeDailyProgress'
import { t } from 'ttag'
import YumInsufficientPermissionsWrapper from '../YumInsufficientPermissions/YumInsufficientPermissionsWrapper'
import { isRtl } from '../../i18nInit'
import YumManualEntryFormWrapper from '../YumManualEntryForm/YumManualEntryFormWrapper'
import { PointsLogic, PointsLogicId } from '../../views/ChallengeAdmin/components/ChallengePoints/ChallengePoints'
import makeStyles from '@material-ui/core/styles/makeStyles'
import YumTotalPointsTableWrapper from '../YumCustomTable/YumTotalPointsTableWrapper'
import YumChallengeLeaderboardRank from '../YumChallengeGoal/components/YumChallengeLeaderboardRank/YumChallengeLeaderboardRank'
import YumManualEntryButtonAndDetails from './YumManualEntryButtonAndDetails'

export interface AllDailyDiary {
  [goalId: string]: UserDailyDiaryProgress[]
}
export interface AllCustomProgress {
  // TODO:
  [goalId: string]: any //UserCustomDailyProgress[]
}

export interface IStateProps {
  userDto: UserProfileDto
  goal: GoalWithProgress
  dailyDiary: UserDailyDiaryProgress[]
  allDailyDiary: AllDailyDiary
  customProgress: UserCustomDailyProgress[]
  allCustomProgress: AllCustomProgress
  progressResults: UserDailyProgress[]
  showManualEntry?: boolean
  isMeView: boolean
  manualEntryProgressType: CustomType
  manualEntryPointsLogicId: PointsLogicId | null
  groupSum: boolean
  isManualEntryInProgress: boolean
  token?: string
  isLegacyRanking?: boolean
  hasRoleInCompany(companyId: string, role: Role): boolean
  divisionCategories: DivisionCategoryRow[]
}

export interface IDispatchProps {
  getDivisionCategories: () => void
}

const useStyles = makeStyles((theme) => ({
  wrapper: {
    // @media (min-width: 992px) {
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      width: '100%',
      //gap: '20px',

      '& > div': {
        width: '50%',
      },
    },
  },
  wrapperMarginRight: {
    [theme.breakpoints.up('md')]: {
      marginRight: isRtl ? 0 : '10px',
      marginLeft: isRtl ? '10px' : 0,
    },
  },
  wrapperMarginLeft: {
    [theme.breakpoints.up('md')]: {
      marginLeft: isRtl ? 0 : '10px',
      marginRight: isRtl ? '10px' : 0,
    },
  },
}))

export default function YumCustomEntry(props: IStateProps & IDispatchProps) {
  const {
    userDto,
    goal,
    dailyDiary,
    customProgress,
    allCustomProgress,
    progressResults,
    isMeView,
    manualEntryProgressType,
    manualEntryPointsLogicId,
    allDailyDiary,
    isManualEntryInProgress,
    token,
    isLegacyRanking,
    hasRoleInCompany,
    divisionCategories,
    getDivisionCategories,
  } = props
  const isAuthenticated = userDto.userId != ''
  const {
    id: goalId,
    customUnit,
    dailyPersistenceGoal,
    progressType,
    participating,
    manualEntryEnabled,
    roundDailyPoints,
    oldRoundDailyPoints,
    companyId,
    endDate,
    dailyMax,
  } = goal
  const customType = goal.customType
  const isCustom = isCustomType(progressType)
  const isCustomCustom = customType === CustomType.CUSTOM
  const isSimpleType = isSimpleDayType(customType as unknown as CustomTypeCategory)
  const isPersistence = isPersistenceType(progressType)
  const isPersistenceCustomOrBinary =
    progressType === ProgressType.PERSISTENCE_CUSTOM || progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY

  const simpleManualEntry = goal.simpleManualEntry && progressType === ProgressType.PERSISTENCE_CUSTOM

  const classes = useStyles()

  const challengeStartDate = dayjs(convertUTCToLocalDate(new Date(goal.startDate)))
  const challengeEndDate = dayjs(convertUTCToLocalDate(new Date(goal.endDate)))

  const challengeHasStarted = challengeStartDate.isBefore(new Date())
  const challengeHasEnded = challengeEndDate.isBefore(new Date())

  const gracePeriodEnd = challengeEndDate.clone().add(3, 'days')
  const gracePeriodEnded = dayjs().isAfter(gracePeriodEnd)

  const isActive = challengeHasStarted && !gracePeriodEnded

  const isActivityIndex = progressType === ProgressType.ALL
  const isAnyActivityIndex = [ProgressType.ALL, ProgressType.PERSISTENCE_ALL].includes(progressType) //progressType === ProgressType.ALL
  const isCollective = isCollectiveType(progressType)

  const userCantManualEntry = isAnyActivityIndex && !userDto.dateOfBirth

  // Double check
  const showLegacyProgress = isAnyActivityIndex // !isCustom //&& !isCollective

  const isPoints = goal.customType === CustomType.POINTS
  const isTotalPoints = goal.customType === CustomType.TOTAL_POINTS
  const pointsLogic = isPoints ? JSON.parse(goal.pointsLogic) : null

  const isCollectiveChallenge = isCollectiveType(progressType)
  const groupSum = props.groupSum !== undefined && props.groupSum !== null ? props.groupSum : isCollectiveChallenge

  // TODO: Migrate and keep only in parent ChallengeMe component?
  const syncProgress = (isLegacyRanking?: boolean) => {
    // console.log('YumCustomEntry:syncProgress | trigger refreshChallengeResults | isLegacyRanking:', isLegacyRanking)
    syncChallengeProgress({
      goal,
      manualEntryProgressType,
      pointsLogic,
      isPoints,
      isMeView,
      groupSum,
      token,
      isLegacyRanking,
    })
  }

  useEffect(() => {
    let appStateChangeListener: PluginListenerHandle | undefined
    const setListener = async () => {
      appStateChangeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          syncProgress(isLegacyRanking)
        }
      })
    }

    setListener().catch(console.error)

    return () => {
      if (appStateChangeListener) {
        try {
          appStateChangeListener.remove()
        } catch (error) {
          console.error('YumCustomEntry | appStateChangeListener.remove() error', error)
        }
      }
    }
  }, [])

  const isFirstRender = useRef(true)

  // Update results after data sync.
  // For "me/leaderboard/ranking" views, the initial trigger is not needed,
  // the challenge data is loaded in the parent component.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      syncProgress()
      return
    }
    // This also acts as the initial trigger.
    //console.log('YumCustomEntry | userDto.lastSync/goal.id hook')
    syncProgress(true)

    // TODO: THIS CAUSES INFINITE LOOP FOR ME VIEW:
    // * because it triggers syncProgress again and again when userDto.lastSync changes
  }, [/*userDto.lastSync, */ goal.id])

  useEffect(() => {
    setShowDetails(false)
  }, [goal.id])

  // Load DivisionCategories when component mounts
  useEffect(() => {
    if (goal.companyId) {
      getDivisionCategories()
    }
  }, [goal.companyId, getDivisionCategories])

  //const units = getCustomUnitText(customType, customType, customUnit) as string
  //const unitsLabel = getCustomUnitLabel(customType, customType, customUnit) as string
  const unitsLabel = getUnit(progressType, customType, customUnit) as string

  const isDistance = isDistanceType(customType)
  const isAutomated = isAutomatedType(customType)

  const isManualEntryEnabled = goal.manualEntryEnabled || isPersistenceCustomOrBinary

  const hasCustomPointActivities =
    pointsLogic &&
    pointsLogic.some(
      (a: PointsLogic) => (a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit)) && !isBonusType(a.category),
    )

  const showConsistencyDailyProgress = isPersistence && (!isCustom || isAutomated) //&& challengeHasEnded

  const showCustomEntry = !isTotalPoints && !isAutomated && isCustom /*&& isManualEntryEnabled*/ //&& challengeHasStarted && !gracePeriodEnded

  //const showManualEntryButton = (isAutomated && manualEntryEnabled) || (!isCustom && manualEntryEnabled) //&& challengeHasStarted && !gracePeriodEnded
  let showManualEntryButton =
    !token && (((isAutomated || !isCustom) && manualEntryEnabled) || hasCustomPointActivities) && !isTotalPoints

  // If it has only hidden "bonus" activities, hide manual entry
  if (showManualEntryButton && isPoints) {
    // Check if the goal has only bonus activities and manual entry is disabled
    // When manual entry is disabled, bonus activities are hidden from regular users
    const hasOnlyHiddenBonusActivities =
      pointsLogic && pointsLogic.length > 0 && pointsLogic.every((a: PointsLogic) => isBonusType(a.category))
    if (hasOnlyHiddenBonusActivities) {
      showManualEntryButton = false
    }
  }

  const tableDataReversed = customProgress ? customProgress.slice().reverse() : customProgress

  let dailyDiaryReversed = dailyDiary ? dailyDiary.slice().reverse() : dailyDiary

  // TODO: Only for activity index
  // Convert "Progress view" data into "Custom" data.
  if (showLegacyProgress) {
    dailyDiaryReversed = progressResults
      .slice()
      .reverse()
      .map((userDailyProgress: UserDailyProgress) => {
        const manualValue = getPersistenceDailyTotal(userDailyProgress, progressType, true)
        const integrationValue = isAnyActivityIndex ? userDailyProgress.integrationTotalPercentage : manualValue
        return {
          date: userDailyProgress.date,
          integrationValue,
          manualValue: manualValue - integrationValue,
          sum: manualValue,
        } as UserDailyDiaryProgress
      })
  }

  const [showDetails, setShowDetails] = useState(false)
  const toggleShowDetails = () => {
    setShowDetails(!showDetails)
  }

  function onSubmit() {
    // Do nothing.
  }

  const isUser = hasRoleInCompany(companyId, Role.USER)

  const customTableValueGoal = getDailyValueGoal(goal, progressType) //progressType === ProgressType.PERSISTENCE_CUSTOM ? dailyPersistenceGoal || 0 : 1

  // Don't close if activity index or points. Except if it has only one POINTS activity...
  const hasOnlyOnePointsLogicActivity =
    pointsLogic &&
    (goal.manualEntryEnabled
      ? pointsLogic.length === 1
      : pointsLogic.filter((a: PointsLogic) => a.unit === CustomType.CUSTOM || !isAutomatedType(a.unit)).length === 1)
  const autoClose =
    !isActivityIndexType(goal.progressType) && (goal.customType !== CustomType.POINTS || hasOnlyOnePointsLogicActivity)

  const showJoinButton = !token && !participating && isUser && !challengeHasEnded

  const dailyMaxValue = dailyMax && dailyMax > 0 && !isPersistence && !isAnyActivityIndex ? dailyMax : null

  return (
    <>
      {isAutomated ? <YumInsufficientPermissionsWrapper /> : null}
      <div className={showConsistencyDailyProgress || showCustomEntry ? classes.wrapper : ''}>
        {showConsistencyDailyProgress ? (
          <div className={classes.wrapperMarginRight}>
            <YumChallengeDailyProgress
              goal={goal}
              dailyDiary={dailyDiary}
              progressResults={progressResults}
              customProgress={customProgress}
            />
          </div>
        ) : null}

        {showCustomEntry ? (
          <div className={classes.wrapperMarginRight} style={{ marginBottom: '16px' }}>
            <YumManualEntryFormWrapper goal={goal} onSubmit={onSubmit} isMeView={isMeView} />
          </div>
        ) : null}
        <div className={showConsistencyDailyProgress || showCustomEntry ? classes.wrapperMarginLeft : ''}>
          {/* 
            YumChallengeGoal can now display different goal types by passing the goalType prop:
            - goalType="individual" - Individual goal
            - goalType="team" - Team goal  
            - goalType="country" - Country goal
            - goalType="group" with groupCategoryId - Group goal for a specific division category
            - goalType="collective" - Collective goal
            If goalType is not provided, it displays the main goal (backward compatible)
          */}
          <YumChallengeGoal
            //goalType="individual"
            goal={goal}
            dailyDiary={dailyDiary}
            customProgress={customProgress}
            progressResults={progressResults}
            showDetails={showDetails}
            toggleShowDetails={toggleShowDetails}
            isMeView={isMeView}
            isLeaderboard={!!token}
          />
          {/* <YumChallengeGoal
            goalType="team"
            goal={goal}
            dailyDiary={dailyDiary}
            customProgress={customProgress}
            progressResults={progressResults}
            showDetails={showDetails}
            toggleShowDetails={toggleShowDetails}
            isMeView={isMeView}
            isLeaderboard={!!token}
          /> */}
        </div>
      </div>

      <YumManualEntryButtonAndDetails
        goal={goal}
        token={token}
        userDto={userDto}
        hasRoleInCompany={hasRoleInCompany}
        isMeView={isMeView}
        isManualEntryInProgress={isManualEntryInProgress}
        isLegacyRanking={isLegacyRanking}
        showDetails={showDetails}
        onShowDetailsChange={setShowDetails}
      />

      <div style={{ marginBottom: '16px' }}>
        {showDetails && (
          <>
            {isTotalPoints ? (
              <YumTotalPointsTableWrapper goalId={goal.id} />
            ) : (isCustomCustom || isPoints) && !showLegacyProgress ? (
              <YumCustomTable
                data={tableDataReversed}
                valueGoal={customTableValueGoal}
                unitsLabel={unitsLabel}
                units={isAnyActivityIndex ? '%' : ''}
                binary={progressType === ProgressType.PERSISTENCE_CUSTOM_BINARY || simpleManualEntry}
                showDigits={isDistance}
                pointsLogic={pointsLogic}
                allDailyDiary={allDailyDiary}
                allCustomProgress={allCustomProgress}
                manualEntryEnabled={manualEntryEnabled}
                roundDailyPoints={roundDailyPoints}
                oldRoundDailyPoints={oldRoundDailyPoints}
                dailyMax={dailyMaxValue}
              />
            ) : (
              <YumLogTable
                data={dailyDiaryReversed}
                valueGoal={isPersistence ? dailyPersistenceGoal || 0 : 1}
                units={isAnyActivityIndex ? t`Activity index` : unitsLabel}
                showDigits={isDistance}
                manualEntryEnabled={manualEntryEnabled}
                suffix={isAnyActivityIndex ? '%' : ''}
                isBinary={isSimpleType || simpleManualEntry}
                dailyMax={dailyMaxValue}
              />
            )}
          </>
        )}
      </div>

      {isActivityIndex ? <YumChallengeActivityIndexProgress goal={goal} /> : null}
      {isCollective || isForceCollectiveTotal(goal.id) ? (
        <YumChallengeCollectiveProgress
          goal={goal}
          isLeaderboard={!!token}
          totalOnly={!isCollective && isForceCollectiveTotal(goal.id)}
        />
      ) : null}
      {isMeView ? (
        <YumChallengeLeaderboardRank goal={goal} groupSum={groupSum} divisionCategories={divisionCategories} />
      ) : null}
    </>
  )
}
